import classNames from 'classnames';
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Logo from '@/components/atoms/logo/logo';
import Button, { ButtonVariant } from '@/components/molecules/button/button';
import ProfilePicture from '@/components/molecules/profile-picture/profile-picture';
import variables from '@/components/organisms/main-navigation/main-navigation.variables';
import MainNavigationLargeMenuItem from '@/components/organisms/main-navigation/main-navigation-large/main-navigation-large-menu-item/main-navigation-large-menu-item';
import MainNavigationLargeMenuItemWrapper from '@/components/organisms/main-navigation/main-navigation-large/main-navigation-large-menu-item-wrapper/main-navigation-large-menu-item-wrapper';
import MainNavigationSharedBrandsDropdown from '@/components/organisms/main-navigation/main-navigation-shared/main-navigation-shared-brands-dropdown/main-navigation-shared-brands-dropdown';
import MainNavigationSharedProfileDropdown from '@/components/organisms/main-navigation/main-navigation-shared/main-navigation-shared-profile-dropdown/main-navigation-shared-profile-dropdown';
import Link from '@/components/utilities/link/link';
import { useFavoritesContext } from '@/libraries/contexts/favorites-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useUserProfileContext } from '@/libraries/contexts/user-profile-context/user-profile-context';
import { getLogoName } from '@/libraries/getters/get-logo-name';
import { getNavigationTheme } from '@/libraries/hooks/use-scroll-theme';
import { MainNavigationMenuItem } from '../main-navigation';
import MainNavigationLargeMenuItemList from './main-navigation-large-menu-item-list/main-navigation-large-menu-item-list';
import styles from './main-navigation-large.module.scss';

export interface MainNavigationLargeProps {
  /** The link to use for booking */
  bookingLink: Content.Properties.Link | null;
  /** The brand of the navigation */
  brand: Content.Properties.Brand;
  /** An array of sub brand items to switch context */
  brandNavigationItems: Content.Properties.BrandNavigationItem[];
  /** The class name to apply */
  className?: string;
  /** The link to use for content search */
  contentSearchPageLink: Content.Properties.Link | null;
  /** The link to use for favorites */
  favoritesPageLink: Content.Properties.Link | null;
  /** The link to use for the logo */
  homePageLink: Content.Properties.Link | null;
  /** Indicates whether the menu dropdown is open */
  isMenuOpen?: boolean;
  /** Indicates whether the profile menu dropdown is open */
  isProfileMenuOpen?: boolean;
  /** Indicates whether the quick menu dropdown is open */
  isQuickMenuOpen?: boolean;
  /** Indicates whether the sub brands dropdown is open */
  isSubBrandsMenuOpen?: boolean;
  /** Indicates if the navigation should be shown */
  isVisible?: boolean;
  /** An array of links to show */
  menuItems?: MainNavigationMenuItem[];
  /** An array of profile menu items */
  profileMenu: Content.Properties.ProfileMenu | null;
  /** Changes the open state of the menu dropdown */
  setIsMenuOpen: Dispatch<SetStateAction<boolean>>;
  /** Changes the open state of the profile dropdown */
  setIsProfileMenuOpen: Dispatch<SetStateAction<boolean>>;
  /** Changes the open state of the quick menu dropdown */
  setIsQuickMenuOpen: Dispatch<SetStateAction<boolean>>;
  /** Changes the state of the sub brands dropdown */
  setIsSubBrandsMenuOpen?: Dispatch<SetStateAction<boolean>>;
}

const SCROLL_UP_THRESHOLD = 16;

const MainNavigationLarge = ({
  bookingLink,
  brand,
  brandNavigationItems = [],
  className,
  contentSearchPageLink,
  favoritesPageLink,
  homePageLink,
  isMenuOpen = false,
  isProfileMenuOpen = false,
  isQuickMenuOpen = false,
  isSubBrandsMenuOpen = false,
  isVisible = false,
  menuItems = [],
  profileMenu,
  setIsMenuOpen,
  setIsProfileMenuOpen,
  setIsQuickMenuOpen,
  setIsSubBrandsMenuOpen,
}: MainNavigationLargeProps) => {
  const { t } = useI18n();
  const {
    hasInPageNavigation,
    hasTransparentNavigation,
    isScrolled,
    setIsScrolled,
    setStickyTopContentHeight,
    shouldUpdateIsScrolled,
  } = usePageDetailsContext();
  const { favoriteCount } = useFavoritesContext();
  const { userProfile } = useUserProfileContext();

  const lastScrollYRef = useRef(0);
  const lastHiddenYRef = useRef(0);
  const lastHashRef = useRef('');
  const shouldHandleScrollRef = useRef<boolean>(shouldUpdateIsScrolled);
  const [navigationTheme, setNavigationTheme] = useState('');
  const [isTransparent, setIsTransparent] = useState(
    !!hasTransparentNavigation,
  );

  useEffect(() => {
    shouldHandleScrollRef.current = shouldUpdateIsScrolled;
  }, [shouldUpdateIsScrolled]);

  useEffect(() => {
    if (isScrolled && !hasInPageNavigation) {
      setStickyTopContentHeight(0);

      return;
    }

    setStickyTopContentHeight(
      variables.mainNavigationLargeHeight +
        variables.mainNavigationLargeMarginTop,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrolled]);

  const formattedQuickMenuItems = useMemo(() => {
    const items: MainNavigationMenuItem[] = [];

    if (contentSearchPageLink) {
      items.push({
        campaignTeaser: null,
        icon: 'Search',
        link: contentSearchPageLink,
        type: 'search',
      });
    }

    if (favoritesPageLink) {
      items.push({
        campaignTeaser: null,
        count: favoriteCount,
        icon: 'HeartFilled',
        link: favoritesPageLink,
        type: 'favorites',
      });
    }

    if (bookingLink) {
      items.push({
        campaignTeaser: null,
        icon: 'User',
        link: bookingLink,
        type: 'user',
      });
    }

    return items;
  }, [bookingLink, contentSearchPageLink, favoriteCount, favoritesPageLink]);

  const formattedMenuItems: MainNavigationMenuItem[] = useMemo(() => {
    if (!menuItems?.length) return [];

    return menuItems.map(item => ({
      ...item,
      type: 'default',
    }));
  }, [menuItems]);

  const handleSubBrandsDropdown = () => {
    setIsSubBrandsMenuOpen?.(!isSubBrandsMenuOpen);
  };

  const handleProfileDropdown = () => {
    setIsProfileMenuOpen?.(!isProfileMenuOpen);
  };

  const handleKeyup = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsProfileMenuOpen?.(false);
      setIsSubBrandsMenuOpen?.(false);
    }
  };

  const handleScroll = () => {
    if (!window) return;

    if (!shouldHandleScrollRef.current) {
      lastHiddenYRef.current = window.scrollY;
      lastScrollYRef.current = window.scrollY;

      return;
    }

    const currentHash = window.location.hash;
    const currentScrollY = window.scrollY;
    const scrollDifference = currentScrollY - lastScrollYRef.current;
    const topContentHeight =
      variables.mainNavigationLargeHeight +
      variables.mainNavigationLargeMarginTop;

    if (scrollDifference > 0 && currentScrollY > topContentHeight) {
      setIsScrolled(true);
      lastHiddenYRef.current = currentScrollY;
    } else if (
      scrollDifference < 0 &&
      lastHiddenYRef.current - currentScrollY >= SCROLL_UP_THRESHOLD &&
      currentHash === lastHashRef.current
    ) {
      const theme = getNavigationTheme();

      theme && setNavigationTheme(theme);
      setIsScrolled(false);
      setIsTransparent(
        !!hasTransparentNavigation && currentScrollY <= topContentHeight,
      );
    }

    lastHashRef.current = currentHash;
    lastScrollYRef.current = currentScrollY;
  };

  const renderProfileMenuItem = () => {
    if (!profileMenu?.items.length) return;

    return (
      <React.Fragment>
        {userProfile?.userProfile.details ? (
          <React.Fragment>
            <button
              aria-expanded={isProfileMenuOpen}
              aria-label={t('items.profile.profilePage.myProfile')}
              className={classNames(
                styles.profilePicture,
                styles.profileButtonDisplay,
              )}
              onClick={handleProfileDropdown}
              type="button"
            >
              <ProfilePicture
                firstName={userProfile.userProfile.details.firstName || ''}
                imageUrl={userProfile.userPicture?.url || undefined}
                lastName={userProfile.userProfile.details.lastName || ''}
                mode="navigationButton"
                size={40}
              />
            </button>

            {/* Visible only in Nortlander to match the main navigation */}
            <MainNavigationLargeMenuItemWrapper
              ariaLabel={t('items.profile.profilePage.myProfile')}
              className={styles.profileMenuItem}
              isPriority
              onClick={event => {
                event?.preventDefault();
                handleProfileDropdown();
              }}
            >
              <ProfilePicture
                className={styles.profilePictureBackground}
                firstName={userProfile.userProfile.details.firstName || ''}
                imageUrl={userProfile.userPicture?.url || undefined}
                lastName={userProfile.userProfile.details.lastName || ''}
                mode="navigationButton"
                size={50}
              />
            </MainNavigationLargeMenuItemWrapper>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Button
              className={styles.profileButton}
              icon="User"
              onClick={handleProfileDropdown}
              variant={
                variables.mainNavigationLargeProfileMenuButtonVariant as ButtonVariant
              }
              {...(profileMenu.text
                ? {
                    iconPosition: 'left',
                    text: profileMenu.text,
                  }
                : {
                    'aria-label': t('items.profile.profilePage.myProfile'),
                  })}
            />

            {/* Visible only in Nortlander to match the main navigation */}
            <MainNavigationLargeMenuItem
              className={styles.profileMenuItem}
              icon="User"
              isPriority
              link={{
                target: '_self',
                text:
                  profileMenu.text || t('items.profile.profilePage.myProfile'),
                url: '',
              }}
              onClick={event => {
                event?.preventDefault();
                handleProfileDropdown();
              }}
            />
          </React.Fragment>
        )}

        <MainNavigationSharedProfileDropdown
          isProfileMenuOpen={isProfileMenuOpen}
          profileMenuItems={profileMenu.items}
        />
      </React.Fragment>
    );
  };

  useEffect(() => {
    if (!window) return;

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('touchmove', handleScroll);
    window.addEventListener('wheel', handleScroll);
    window.addEventListener('keyup', handleKeyup);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('keyup', handleKeyup);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      aria-hidden={!isVisible}
      className={classNames(styles.component, className, {
        [styles.isNotTransparent]:
          !isTransparent ||
          isMenuOpen ||
          isProfileMenuOpen ||
          isQuickMenuOpen ||
          isSubBrandsMenuOpen,
        [styles.isScrolled]: isScrolled,
        [styles.isVisible]: isVisible,
      })}
      data-theme={navigationTheme}
    >
      <nav
        aria-label={t('items.common.words.navigation')}
        className={styles.navigation}
      >
        {brandNavigationItems.length > 0 && (
          <Button
            aria-label={t('items.campaign.searchBar.brand.chooseBrand')}
            className={styles.subBrandsButton}
            icon={isSubBrandsMenuOpen ? 'ChevronUp' : 'ChevronDown'}
            onClick={handleSubBrandsDropdown}
            variant="ghost"
          />
        )}

        {homePageLink && (
          <Link
            aria-label={t('items.common.words.homepage')}
            className={styles.logo}
            url={homePageLink.url}
          >
            <Logo
              name={getLogoName({
                brand,
                type: 'Navigation',
              })}
              title={homePageLink.text}
            />
          </Link>
        )}

        <div className={styles.links}>
          <MainNavigationLargeMenuItemList
            className={styles.menuItems}
            isOpen={isMenuOpen}
            isTransparent={
              isTransparent &&
              !isMenuOpen &&
              !isProfileMenuOpen &&
              !isQuickMenuOpen &&
              !isSubBrandsMenuOpen
            }
            items={formattedMenuItems}
            onClose={() => setIsMenuOpen(false)}
            onOpen={() => setIsMenuOpen(true)}
          />

          <MainNavigationLargeMenuItemList
            className={styles.quickMenuItems}
            isOpen={isQuickMenuOpen}
            isPriority
            isQuickMenu
            isTransparent={
              isTransparent &&
              !isMenuOpen &&
              !isProfileMenuOpen &&
              !isQuickMenuOpen &&
              !isSubBrandsMenuOpen
            }
            items={formattedQuickMenuItems}
            onClose={() => setIsQuickMenuOpen(false)}
            onOpen={() => setIsQuickMenuOpen(true)}
          >
            {renderProfileMenuItem()}
          </MainNavigationLargeMenuItemList>
        </div>

        {brandNavigationItems.length > 0 && (
          <MainNavigationSharedBrandsDropdown
            brandNavigationItems={brandNavigationItems}
            isSubBrandsMenuOpen={isSubBrandsMenuOpen}
          />
        )}
      </nav>
    </header>
  );
};

MainNavigationLarge.displayName = 'MainNavigationLarge';

export default MainNavigationLarge;
